import { inject, Injectable, OnInit } from '@angular/core';

import { TranslationService } from 'app/core/internationalization/translation.service';

export type InputRequirements = 'minLength' | 'allVisibleChars' | 'oneUpper' | 'oneLower' | 'oneNumber' | 'oneSpecial';

@Injectable({
  providedIn: 'root'
})
export class PasswordRequirementsService implements OnInit {
  private readonly _translationService = inject(TranslationService);

  public requirementKeys: InputRequirements[] = [
    'minLength', 'allVisibleChars', 'oneUpper', 'oneLower', 'oneNumber', 'oneSpecial'
  ];

  private requirements: Map<InputRequirements, boolean> = new Map();
  private minPasswordLength = 8;
  private numberRegexCheck = /\d/;
  private specialCharacterRegexCheck = /[!@#$%^&*]+/;
  public requiredFieldMessage = 'This field is required, please enter a valid input';
  public requirementMessages!: { [key in InputRequirements]: string };

  // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility
  constructor() {
    this.requiredFieldMessage = this._translationService.instant(this.requiredFieldMessage);
    this.translateRequirementMessages();
  }

  public ngOnInit() {
    this.resetAllRequirements();
  }

  public resetAllRequirements(): void {
    this.requirementKeys.forEach((key: InputRequirements) => {
      this.requirements.set(key, false);
    });
  }

  public getRequiredFieldMessage(): string {
    return this.requiredFieldMessage;
  }

  public getAllRequirementMessages(): { [key in InputRequirements]: string } {
    return this.requirementMessages;
  }

  public getSingleRequirementMessage(requirement: InputRequirements): string {
    return this.requirementMessages[requirement];
  }

  public getAllRequirementKeys(): InputRequirements[] {
    return this.requirementKeys;
  }

  public isAllRequirementsMet(): boolean {
    return this.requirementKeys.every((key: InputRequirements) => this.isRequirementMet(key));
  }

  public isRequirementMet(key: InputRequirements): boolean {
    return this.requirements.get(key) || false;
  }

  private translateRequirementMessages(): void {
    this.requirementMessages = {
      minLength: this._translationService.instant('PASSWORD_REQUIREMENTS.MINIMUM_OF_PASSWORDLENGTH_CHARACTERS_WITH', { passwordLength: this.minPasswordLength }),
      allVisibleChars: this._translationService.instant('PASSWORD_REQUIREMENTS.NO_SPACES_ALLOWED'),
      oneUpper: this._translationService.instant('PASSWORD_REQUIREMENTS.AT_LEAST_ONE_UPPERCASE_LETTER'),
      oneLower: this._translationService.instant('PASSWORD_REQUIREMENTS.AT_LEAST_ONE_LOWERCASE_LETTER'),
      oneNumber: this._translationService.instant('PASSWORD_REQUIREMENTS.AT_LEAST_ONE_NUMBER'),
      oneSpecial: this._translationService.instant('PASSWORD_REQUIREMENTS.AT_LEAST_ONE_OF_THE')
    };
  }

  public updateCandidate(candidatePassword: string): void {
    this.setMinLengthRequirementMet(candidatePassword);
    this.setAllVisibleCharactersMet(candidatePassword);
    this.requirements.set('oneUpper', candidatePassword.toLowerCase() !== candidatePassword);
    this.requirements.set('oneLower', candidatePassword.toUpperCase() !== candidatePassword);
    this.requirements.set('oneNumber', this.numberRegexCheck.test(candidatePassword));
    this.requirements.set('oneSpecial', this.specialCharacterRegexCheck.test(candidatePassword));
  }

  public setMinLengthRequirementMet(candidatePassword: string): void {
    const isMinLengthMet: boolean = candidatePassword.length >= this.minPasswordLength;
    this.requirements.set('minLength', isMinLengthMet);
  }

  public setAllVisibleCharactersMet(candidatePassword: string): void {
    const hasSpaces: boolean = candidatePassword.indexOf(' ') >= 0;
    this.requirements.set('allVisibleChars', !hasSpaces);
  }
}
